import { useEffect, useState } from 'react';
import { isIosLike, isStandalone } from './platform';

/**
 * «Установить приложение».
 *
 * The event has to be captured at startup, not when a component mounts: Chrome fires
 * `beforeinstallprompt` once, very early, and if nothing calls `preventDefault()` on it in time
 * the browser shows its own mini-infobar and the event is gone. So `captureInstallPrompt()` runs
 * from `main.tsx` and parks the event here; the UI asks for it later, whenever it is ready.
 *
 * On iOS there is no event and no API — installing is a manual trip through the share sheet, so
 * all we can offer is the instruction. That asymmetry is why this exposes a *mode* rather than a
 * boolean: the two platforms need two different pieces of UI, not the same button.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'tt:install-dismissed';

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own bar, and the app cannot choose the moment to ask.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    // Nothing left to offer, and the banner should go even before the display mode changes.
    remember();
    notify();
  });
}

function remember(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Storage disabled. The banner reappears next visit; that is a nuisance, not a fault.
  }
}

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Unable to remember a refusal — better to stay quiet than to nag on every load.
    return true;
  }
}

export type InstallMode = 'none' | 'native' | 'ios';

export interface InstallOffer {
  mode: InstallMode;
  /** Chrome's own dialog. Resolves once the person has answered it, either way. */
  install: () => Promise<void>;
  /** «Не сейчас» — remembered, so it is asked once and not on every visit. */
  dismiss: () => void;
}

export function useInstallOffer(): InstallOffer {
  const [, forceUpdate] = useState(0);
  const [dismissed, setDismissed] = useState(() => wasDismissed());

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  let mode: InstallMode = 'none';
  if (!dismissed && !isStandalone()) {
    if (deferred !== null) mode = 'native';
    else if (isIosLike(navigator.userAgent, navigator.maxTouchPoints)) mode = 'ios';
  }

  return {
    mode,
    install: async () => {
      const event = deferred;
      if (event === null) return;
      // The event is single-use: whatever the answer, it cannot be shown twice.
      deferred = null;
      await event.prompt();
      await event.userChoice;
      remember();
      setDismissed(true);
    },
    dismiss: () => {
      remember();
      setDismissed(true);
    },
  };
}
